import { useState, useMemo, useEffect } from 'react';
import { PROSPECTS_LABELS_ID } from '../../../config/constants';
import { Box, Typography, Loader, Button } from '@lucid/lego';
import { useStore } from '../../../store/store';
import { useLabels } from '../../../hooks/useLabels';
import { convertToContentfulLocale } from '../../../utils/tools';
import Table from './Table/Table';
import Configurator from '../../../components/Configurator/Configurator';
import { useProspectsTable } from './hooks/useProspectsTable';
import { useProspectUrlState } from './hooks/useProspectUrlState';
import styles from './Prospects.module.scss';
import Checkout from '../../../components/Checkout/Checkout';
import type { InventoryTableRow } from '../../../components/Configurator/utils';
import { useGetQuoteById } from '../../../api/services/lupp/quotes/quotes';

const Prospects: React.FC = () => {
  const theme = useStore((state) => state.theme);
  const locale = useStore((state) => state.locale);
  const selectedQuote = useStore((state) => state.selectedQuote);
  const clearSelectedProspect = useStore((state) => state.clearSelectedProspect);
  const clearSelectedQuote = useStore((state) => state.clearSelectedQuote);
  const [showVehicleDetails, setShowVehicleDetails] = useState(false);

  // Labels
  const {
    data: labels,
    isLoading: labelsLoading,
    error: labelsError,
  } = useLabels({
    id: PROSPECTS_LABELS_ID,
    locale: convertToContentfulLocale(locale),
  });

  // URL state management
  const {
    selectedProspectId,
    activeTab,
    showConfigurator,
    showCheckout,
    checkoutParams,
    quoteIdFromUrl,
    hasInitialProspectInUrl,
    selectedProspectData,
    onProspectSelect,
    onTabChange,
    onCreateQuotation,
    onCreateProspect,
    onBackToProspects,
    onCheckoutNavigate,
    onShortcodeNavigate,
    onCheckoutBack,
    onBackToProspectDetails,
    onQuoteClick,
  } = useProspectUrlState();

  // Table state management - disable when in configurator/checkout view
  const {
    data: prospectsData,
    totalCount: prospectsTotalCount,
    isLoading,
    isFetching,
    error,
    currentPage,
    pageSize,
    searchQuery,
    debouncedSearch,
    sortField,
    sortOrder,
    onPageChange,
    onPageSizeChange,
    onSearchChange,
    onSort,
  } = useProspectsTable({
    enabled: !labelsLoading && !labelsError && !showConfigurator,
  });

  // Merge table data with single prospect view data
  const tableData = useMemo(() => {
    if (hasInitialProspectInUrl && selectedProspectData?.prospectDetails) {
      return [selectedProspectData.prospectDetails];
    }
    return prospectsData;
  }, [prospectsData, hasInitialProspectInUrl, selectedProspectData]);

  const totalCount = useMemo(() => {
    if (hasInitialProspectInUrl && selectedProspectData?.prospectDetails) {
      return 1;
    }
    return prospectsTotalCount;
  }, [hasInitialProspectInUrl, selectedProspectData, prospectsTotalCount]);

  // Configurator handlers
  const handleBackToProspects = () => {
    setShowVehicleDetails(false);
    clearSelectedProspect();
    onBackToProspects();
  };

  const handleCheckoutBack = () => {
    setShowVehicleDetails(false);
    onCheckoutBack();
  };

  // Clear selected quote on mount
  useEffect(() => {
    clearSelectedQuote();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleBackToInventory = () => {
    clearSelectedQuote();
    onBackToProspectDetails('quotations');
  };

  const prospectId = selectedProspectId ?? '';
  const firstName = selectedProspectData?.prospectDetails?.prospectFirstName ?? '';
  const lastName = selectedProspectData?.prospectDetails?.prospectLastName ?? '';
  const email = selectedProspectData?.prospectDetails?.prospectEmail ?? '';
  const prospectName = firstName || lastName ? `${firstName} ${lastName}`.trim() : '';
  // Use quoteId from URL (for refresh) or from selected quote (from click)
  const quoteId = quoteIdFromUrl ?? selectedQuote?.quote_identifier ?? '';

  const { data: quoteData, isLoading: isQuoteLoading } = useGetQuoteById({
    prospectId,
    quoteId,
    enabled: !!prospectId && !!quoteId,
  });

  const detailsVehicle = {
    vin: quoteData?.vin ?? '',
    batchNumber: quoteData?.vinBatchNumber ?? '',
  } as InventoryTableRow;

  const prospectDetails = {
    prospectId,
    prospectName,
    quoteId,
    firstName,
    lastName,
    email,
    stateProvince: selectedProspectData?.prospectDetails?.prospectAddressDetail?.stateProvince,
  };

  // Show checkout when we have a quote (from URL or from selection)
  if (selectedQuote || quoteIdFromUrl) {
    return (
      <Checkout
        vehicle={detailsVehicle}
        checkoutData={quoteData ?? null}
        isLoading={isQuoteLoading}
        onBack={handleBackToInventory}
        dataTestId={`${quoteId}-checkout`}
        prospectDetails={prospectDetails}
        docusignStatus={quoteData?.docusignStatus}
        currentStatus={quoteData?.currentStatus}
        onQuoteCreated={onQuoteClick}
      />
    );
  }

  // Render configurator view
  if (showConfigurator) {
    // Hide header/description when in checkout mode or showing vehicle details
    const hideHeader = showVehicleDetails || showCheckout;

    return (
      <Box>
        {!hideHeader && (
          <Box mb={16}>
            <Button
              type="tertiary"
              color="neutral"
              theme={theme}
              dataTestId="back-to-prospects-button"
              gaComponent="prospects"
              label={labels?.['list.backButton'] ?? 'Back to Prospects'}
              iconLeft={{ category: 'arrows', name: 'arrow-left' }}
              handleClick={handleBackToProspects}
            />
          </Box>
        )}
        {!hideHeader && (
          <>
            <Typography variant="headline" size="small" color="primary-1" theme={theme}>
              {prospectName
                ? (labels?.['prospects.quotationTitle.withName']?.replace(
                    '{{prospectName}}',
                    prospectName
                  ) ?? `Quotation for ${prospectName}`)
                : (labels?.['prospects.quotationTitle.default'] ?? 'Quotation')}
            </Typography>
            <Typography variant="body" size="medium" color="gray-3" theme={theme}>
              {labels?.['prospects.savedQuotationsNote'] ??
                'Saved quotations can be reviewed in prospect details.'}
            </Typography>
          </>
        )}
        <Box pt={hideHeader ? 0 : 24}>
          <Configurator
            dataTestId="prospect-configurator"
            showConfigureTab
            onDetailsViewChange={setShowVehicleDetails}
            checkoutParams={checkoutParams}
            onCheckoutNavigate={onCheckoutNavigate}
            onShortcodeNavigate={onShortcodeNavigate}
            onCheckoutBack={handleCheckoutBack}
            prospectDetails={prospectDetails}
            onQuoteCreated={onQuoteClick}
          />
        </Box>
      </Box>
    );
  }

  const invertedTheme = theme === 'dark' ? 'light' : 'dark';

  // Render loading state
  if (labelsLoading || isLoading) {
    return (
      <Box>
        <Typography variant="headline" size="small" color="primary-1" theme={theme}>
          {labels?.['title'] ?? 'Prospects'}
        </Typography>
        <Typography variant="body" size="medium" color="gray-3" theme={theme}>
          {labels?.['description'] ?? 'View the status of existing prospects'}
        </Typography>
        <Box className={styles.loaderContainer}>
          <Loader size={48} theme={theme} />
        </Box>
      </Box>
    );
  }

  // Render error state
  if (labelsError) {
    return (
      <Box>
        <Box pt={24}>
          <Typography variant="body" size="large" theme={theme} color="error-1">
            {labels?.error_loading_labels}
          </Typography>
        </Box>
      </Box>
    );
  }

  if (error && !searchQuery && !debouncedSearch && !isFetching) {
    return (
      <Box>
        <Typography variant="headline" size="small" color="primary-1" theme={theme}>
          {labels?.['title'] ?? 'Prospects'}
        </Typography>
        <Typography variant="body" size="medium" color="gray-3" theme={theme}>
          {labels?.['description'] ?? 'View the status of existing prospects'}
        </Typography>
        <Box pt={24}>
          <Typography variant="body" size="large" theme={theme} color="error-1">
            {labels?.error_loading_prospects}
          </Typography>
        </Box>
      </Box>
    );
  }

  // Render table view
  return (
    <Box>
      <Typography variant="headline" size="small" color="primary-1" theme={theme}>
        {labels?.['title'] ?? 'Prospects'}
      </Typography>
      <Typography variant="body" size="medium" color="gray-3" theme={theme}>
        {labels?.['description'] ?? 'View the status of existing prospects'}
      </Typography>
      <Box pt={24} className={styles.loadingContainer}>
        {isFetching && (
          <Box className="fetchingOverlay">
            <Loader size={48} theme={invertedTheme} />
          </Box>
        )}
        <Table
          theme={theme}
          data={error ? [] : tableData}
          totalCount={error ? 0 : totalCount}
          currentPage={currentPage}
          pageSize={pageSize}
          onPageChange={onPageChange}
          onPageSizeChange={onPageSizeChange}
          labels={labels || {}}
          searchQuery={searchQuery}
          onSearchChange={onSearchChange}
          onCreateQuotation={onCreateQuotation}
          onCreateProspect={onCreateProspect}
          sortColumn={sortField}
          sortDirection={sortOrder}
          onSort={onSort}
          selectedProspectId={selectedProspectId}
          onProspectSelect={onProspectSelect}
          activeTab={activeTab}
          onTabChange={onTabChange}
          onQuoteClick={onQuoteClick}
          locale={locale}
        />
      </Box>
    </Box>
  );
};

export default Prospects;
